import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { CheckCircle2, Sparkles, UserCheck, ShieldCheck } from "lucide-react";
import image1 from '../../Assets/Services/Service1-2.jpg'
import image2 from '../../Assets/Services/Service1-3.jpg'
import { Helmet } from "react-helmet";


const SmileDesigning = () => {
  return (
    <ServiceLayout
      title="Smile Designing & Cosmetic Dentistry"
      subtitle="Smile with confidence again – achieve a flawless, natural-looking smile with our expert cosmetic dentists in Vijayawada & Hyderabad."
      heroImage="/images/hero/smile-design.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <Helmet>
  <title>Smile Designing Vijayawada | Cosmetic Care</title>
  <meta name="description" content="Get perfect smile designing in Vijayawada with advanced cosmetic dentistry and smile makeover solutions by experts." />
  <meta name="keywords" content="smile designing Vijayawada, cosmetic dentist" />
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sridhardental.com/service/smile-Designing" />
</Helmet>

        <p className="text-lg text-gray-700 leading-relaxed">
          A beautiful smile can transform the way you look and feel. With advanced cosmetic dentistry,
achieving a confident, natural-looking smile is now easier than ever. At Dr. Sridhar International
Dental Hospitals, we specialize in smile designing and cosmetic dental treatments that enhance
both the appearance of your smile and your overall confidence.
        </p>
        <p className="mt-4 text-gray-700">
          Our experienced cosmetic dentists in Vijayawada and Hyderabad use modern technology and
advanced aesthetic techniques to correct imperfections, restore damaged teeth, and create
stunning, natural smiles that you’ll love to share every day.
        </p>
      </section>

      {/* What is Smile Designing Section */}
      <section className="bg-blue-50 p-8 rounded-2xl mb-12">
        <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center gap-2">
          <Sparkles className="text-blue-600" /> What is Smile Designing?
        </h2>
        <p className="text-gray-700 mb-6">
          Smile designing is an advanced cosmetic dental treatment that enhances the beauty and balance of
your smile. It focuses on improving the appearance of your teeth while ensuring they look natural
and harmonious with your facial features.
        </p>
        <p>
            Our dentists carefully evaluate several important factors to create your perfect smile:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Tooth color",
            "Tooth shape and size",
            "Alignment and spacing of teeth",
            "Gum contour",
            "Facial symmetry and lip line",
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-blue-100">
              <CheckCircle2 className="text-blue-500 w-5 h-5" />
              <span className="font-medium text-gray-800">{item}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-blue-800 italic font-medium">
          Based on these factors, our dentists create a personalized smile makeover plan to achieve a balanced, healthy, and aesthetically pleasing smile.
        </p>
      </section>

      {/* Ideal Candidates Grid */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Smile designing is ideal for patients who have:</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {[
            "Discolored or stained teeth",
            "Chipped or broken teeth",
            "Uneven or irregular teeth",
            "Gaps between teeth",
            "Worn-down teeth",
            "Misaligned teeth",
          ].map((condition, idx) => (
            <div key={idx} className="border-l-4 border-blue-600 pl-4 py-2 hover:bg-gray-50 transition">
              <p className="font-semibold text-gray-800">{condition}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Treatments Section */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-blue-900 mb-8">Cosmetic Dental Treatments We Provide</h2>
        
        <div className="space-y-8">
          {/* Composite Bonding */}
          <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
            <h3 className="text-xl font-bold !text-blue-700 mb-3">Composite Bonding</h3>
            <p className="text-gray-700 mb-4">
              A minimally invasive cosmetic procedure used to repair minor dental imperfections. A tooth-colored composite resin is applied and shaped to restore the natural appearance.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Chipped teeth", "Small gaps", "Shape correction"].map((tag) => (
                <span key={tag} className="text-xs font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Dental Crowns */}
          <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
            <h3 className="text-xl font-bold !text-blue-700 mb-3">Dental Crowns</h3>
            <p className="text-gray-700 mb-4">
              Custom-made caps placed over damaged teeth. We use high-quality <strong>ceramic and zirconia crowns</strong> that provide durability and a natural tooth-like appearance.
            </p>
          </div>

          {/* Dental Veneers */}
          <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
            <h3 className="text-xl font-bold !text-blue-700 mb-3">Dental Veneers</h3>
            <p className="text-gray-700 mb-4">
              Thin shells made of porcelain or ceramic bonded to the front surface. One of the most effective treatments for a "Perfect Smile."
            </p>
          </div>
        </div>
      </section>

      {/* Benefits ribbon */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-12 shadow-xl">
        <h2 className="text-2xl font-bold mb-8 text-center !text-white">Benefits of Cosmetic Dentistry</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="flex flex-col items-center text-center">
            <Sparkles className="w-10 h-10 mb-4 text-blue-300" />
            <p className="font-semibold !text-white">Brighter & More Attractive Smile</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <UserCheck className="w-10 h-10 mb-4 text-blue-300" />
            <p className="font-semibold !text-white">Increased Self-Confidence</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <ShieldCheck className="w-10 h-10 mb-4 text-blue-300" />
            <p className="font-semibold !text-white">Long-lasting Natural Results</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="border-t pt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Dr. Sridhar International Dental Hospitals?</h2>
        <p className="text-gray-700">
          At Dr. Sridhar International Dental Hospitals, we are known for providing advanced dental care and
aesthetic dentistry services in Vijayawada and Hyderabad. Our experienced specialists use modern
technology and advanced techniques to deliver safe, high-quality cosmetic dental treatments.
        </p>
        <p className="text-gray-700">
          We focus on enhancing both the beauty and health of your smile. Every patient receives a
personalized smile design that complements their facial features, helping them achieve a natural,
confident, and beautiful smile.
        </p>
      </section>
    </ServiceLayout>
  );
};

export default SmileDesigning;